// src/config/index.ts
var WORD_TRANSLATION_PROVIDER_ID = "com.openai.gpt-4o-mini-2024-07-18";
var APP_NAME = "Crius";

// src/enums/const.ts
var Constants = /* @__PURE__ */ ((Constants2) => {
  Constants2["ACCESS_TOKEN"] = "access_token";
  Constants2["USERNAME"] = "username";
  return Constants2;
})(Constants || {});

// src/enums/content-models.ts
var ContentModel = /* @__PURE__ */ ((ContentModel2) => {
  ContentModel2["NOVEL"] = "novel";
  ContentModel2["LEARNING"] = "learning";
  return ContentModel2;
})(ContentModel || {});

// src/enums/mime-type.ts
var MimeType = /* @__PURE__ */ ((MimeType2) => {
  MimeType2["AAC"] = "audio/aac";
  MimeType2["APNG"] = "image/apng";
  MimeType2["AVIF"] = "image/avif";
  MimeType2["AVI"] = "video/x-msvideo";
  MimeType2["BIN"] = "application/octet-stream";
  MimeType2["BMP"] = "image/bmp";
  MimeType2["CSV"] = "text/csv";
  MimeType2["DOC"] = "application/msword";
  MimeType2["DOCX"] = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  MimeType2["GIF"] = "image/gif";
  MimeType2["HTML"] = "text/html";
  MimeType2["JPEG"] = "image/jpeg";
  MimeType2["JSON"] = "application/json";
  MimeType2["JSONLD"] = "application/ld+json";
  MimeType2["MIDI"] = "audio/midi";
  MimeType2["MP3"] = "audio/mpeg";
  MimeType2["MP4"] = "video/mp4";
  MimeType2["MPEG"] = "video/mpeg";
  MimeType2["OGA"] = "audio/ogg";
  MimeType2["OGV"] = "video/ogg";
  MimeType2["OGX"] = "application/ogg";
  MimeType2["PNG"] = "image/png";
  MimeType2["PDF"] = "application/pdf";
  MimeType2["SVG"] = "image/svg+xml";
  MimeType2["TIFF"] = "image/tiff";
  MimeType2["TS"] = "video/mp2t";
  MimeType2["TXT"] = "text/plain";
  MimeType2["WAV"] = "audio/wav";
  MimeType2["WEBA"] = "audio/webm";
  MimeType2["WEBM"] = "video/webm";
  MimeType2["WEBP"] = "image/webp";
  MimeType2["XHTML"] = "application/xhtml+xml";
  MimeType2["XML_APPLICATION"] = "application/xml";
  MimeType2["XML_TEXT"] = "text/xml";
  MimeType2["XUL"] = "application/vnd.mozilla.xul+xml";
  MimeType2["ZIP"] = "application/zip";
  MimeType2["Z7Z"] = "application/x-7z-compressed";
  MimeType2["GP3"] = "video/3gpp";
  MimeType2["GP2"] = "video/3gpp2";
  MimeType2["MD"] = "text/markdown";
  MimeType2["DITA"] = "application/dita+xml";
  return MimeType2;
})(MimeType || {});

// src/enums/statistic.ts
var Statistic = /* @__PURE__ */ ((Statistic2) => {
  Statistic2["TEST_ENVIRONMENT"] = "test-environment";
  return Statistic2;
})(Statistic || {});

// src/enums/task.ts
var Task = /* @__PURE__ */ ((Task2) => {
  Task2["EXTRACT_STRUCTURE"] = "extract-structure";
  Task2["TEXT_TRANSLATION"] = "text-translation";
  Task2["DESCRIBE_IMAGE"] = "describe-image";
  Task2["GENERATE_AUDIO"] = "generate-audio";
  return Task2;
})(Task || {});
var TASK_COLOR = {
  ["extract-structure" /* EXTRACT_STRUCTURE */]: "#ca5c50",
  ["text-translation" /* TEXT_TRANSLATION */]: "#3c78c5",
  ["generate-audio" /* GENERATE_AUDIO */]: "#3d8f6d",
  ["describe-image" /* DESCRIBE_IMAGE */]: "#f3b43f"
};

// src/formatter/bytes.ts
var formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) {
    return "0 Bytes";
  }
  const kilo = 1024;
  const dm = Math.max(0, decimals);
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
  const i = Math.floor(Math.log(bytes) / Math.log(kilo));
  const value = bytes / kilo ** i;
  return `${parseFloat(value.toFixed(dm))} ${sizes[i]}`;
};

// src/formatter/date.ts
import { DateTime } from "luxon";
var dateTime = (date3) => DateTime.fromJSDate(date3).toFormat("dd/MM/yyyy HH:mm");
var date = (_date) => DateTime.fromJSDate(_date).toFormat("dd/MM/yyyy");
var time = (_date) => DateTime.fromJSDate(_date).toFormat("HH:mm");

// src/formatter/metrics.ts
var DEFAULT = `N/A`;
var metrics = {
  cost: (cost) => {
    if (typeof cost === "number") {
      return `\u20AC ${cost.toFixed(4)}`;
    }
    return DEFAULT;
  },
  time: (time2) => {
    if (typeof time2 === "number") {
      return `${(time2 / 1e3).toFixed(2)}s`;
    }
    return DEFAULT;
  },
  bleu: (bleu) => {
    if (typeof bleu === "number") {
      return `${bleu.toFixed(2)}`;
    }
    return DEFAULT;
  },
  cer: (cer) => {
    if (typeof cer === "number") {
      return `${cer.toFixed(2)}`;
    }
    return DEFAULT;
  },
  wer: (wer) => {
    if (typeof wer === "number") {
      return `${wer.toFixed(2)}`;
    }
    return DEFAULT;
  },
  comet: (comet) => {
    if (typeof comet === "number") {
      return `${comet.toFixed(2)}`;
    }
    return DEFAULT;
  },
  wordsPerMinute: (wordsPerMinute) => {
    if (typeof wordsPerMinute === "number") {
      return `${wordsPerMinute.toFixed(2)}`;
    }
    return DEFAULT;
  },
  wordErrorRate: (wordErrorRate) => {
    if (typeof wordErrorRate === "number") {
      return `${Number(wordErrorRate * 100).toFixed(2)}%`;
    }
    return DEFAULT;
  },
  structuralDistance: (structuralDistance) => {
    if (typeof structuralDistance === "number") {
      return `${structuralDistance.toFixed(2)}`;
    }
    return DEFAULT;
  },
  acuCoverage: (acuCoverage) => {
    if (typeof acuCoverage === "number") {
      return `${acuCoverage.toFixed(2)}`;
    }
    return DEFAULT;
  }
};

// src/formatter/index.ts
var formatter = {
  date: {
    dateTime,
    date,
    time
  },
  bytes: {
    format: formatBytes
  },
  percentage: (score) => `${(score * 100).toFixed(2)}%`,
  metrics
};

// src/metrics/describe-image.metrics.ts
import { z as z2 } from "zod";

// src/metrics/general.metrics.ts
import { z } from "zod";
var baseMetric = z.object({
  cost: z.number().optional().nullable().default(0),
  time: z.number().optional().nullable().default(0)
});

// src/metrics/describe-image.metrics.ts
var describeImageMetrics = z2.object({
  bleu: z2.number().optional().nullable().default(0),
  acuCoverage: z2.number().optional().nullable().default(0)
}).extend(baseMetric.shape);
var isDescribeImageMetrics = (data) => describeImageMetrics.safeParse(data).success;

// src/metrics/extract-structure.metrics.ts
import { z as z3 } from "zod";
var extractStructureMetrics = z3.object({
  cer: z3.number().optional().nullable().default(0),
  wer: z3.number().optional().nullable().default(0),
  structuralDistance: z3.number().optional().nullable().default(0)
}).extend(baseMetric.shape);
var isExtractStructureMetrics = (data) => extractStructureMetrics.safeParse(data).success;

// src/metrics/generate-audio.metrics.ts
import { z as z4 } from "zod";
var generateAudioMetrics = z4.object({
  wordsPerMinute: z4.number().optional().nullable().default(0),
  wordErrorRate: z4.number().optional().nullable().default(0)
}).extend(baseMetric.shape);
var isGenerateAudioMetrics = (data) => generateAudioMetrics.safeParse(data).success;

// src/metrics/text-translation.metrics.ts
import { z as z5 } from "zod";
var textTranslationMetrics = z5.object({
  bleu: z5.number().optional().nullable().default(0),
  comet: z5.number().optional().nullable().default(0)
}).extend(baseMetric.shape);
var isTextTranslationMetrics = (data) => textTranslationMetrics.safeParse(data).success;

// src/parameters/describe-image.parameters.ts
import { z as z6 } from "zod";
var describeImageParameters = z6.object({
  prompt: z6.string().optional(),
  languageCode: z6.string(),
  context: z6.string().optional(),
  description: z6.string().optional()
});
var isDescribeImageParameters = (data) => describeImageParameters.safeParse(data).success;

// src/parameters/extract-structure.parameters.ts
import { z as z7 } from "zod";
var extractStructureParameters = z7.object({
  prompt: z7.string().optional().default("Convert this into DITA XML"),
  reference: z7.string().optional(),
  contentModel: z7.enum(
    Object.values(ContentModel)
  )
});
var isExtractStructureParameters = (data) => extractStructureParameters.safeParse(data).success;

// src/parameters/generate-audio.parameters.ts
import { z as z10 } from "zod";

// src/types/date.ts
import { z as z8 } from "zod";
var date2 = z8.union([z8.date(), z8.string()]).transform((_date) => {
  if (typeof _date === "string") {
    return new Date(_date).toISOString();
  }
  return _date.toISOString();
});

// src/types/dict.ts
import { z as z9 } from "zod";
var dict = z9.record(
  z9.string(),
  z9.union([z9.string(), z9.number(), z9.boolean()])
);

// src/types/env.ts
var ServerEnv = {
  PORT: "PORT",
  JWT_SECRET: "JWT_SECRET",
  AWS_REGION: "AWS_REGION",
  AWS_S3_BUCKET: "AWS_S3_BUCKET",
  MODELS_S3_BUCKET: "MODELS_S3_BUCKET",
  MODELS_S3_KEY: "MODELS_S3_KEY",
  SQS_BACKEND_URL: "SQS_BACKEND_URL",
  POSTMARK_API_KEY: "POSTMARK_API_KEY",
  POSTMARK_SENDER: "POSTMARK_SENDER",
  FRONTEND_URL: "FRONTEND_URL",
  BACKEND_URL: "BACKEND_URL",
  NODE_ENV: "NODE_ENV"
};
var SQSConsumerEnv = {
  PORT: "PORT",
  AWS_SQS_REQUEST_QUEUE: "AWS_SQS_REQUEST_QUEUE",
  AWS_SQS_RESPONSE_QUEUE: "AWS_SQS_RESPONSE_QUEUE",
  AWS_REGION: "AWS_REGION",
  BACKEND_URL: "BACKEND_URL",
  NODE_ENV: "NODE_ENV",
  ENVIRONMENT: "ENVIRONMENT"
};

// src/types/language.ts
import { isValidBCP47 } from "@reacture-io/iso-locale/languages";
var isValidLanguage = (lang) => typeof lang === "string" && isValidBCP47(lang);

// src/types/s3.ts
var isS3File = (file) => typeof file === "object" && file !== null && "s3" in file && typeof file.s3 === "object";

// src/parameters/generate-audio.parameters.ts
var audioGenerationParameters = z10.object({
  prompt: z10.string().optional(),
  text: z10.string(),
  languageCode: z10.custom(isValidLanguage)
});
var isAudioGenerationParameters = (data) => audioGenerationParameters.safeParse(data).success;

// src/parameters/text-translation.parameters.ts
import { z as z11 } from "zod";
var textTranslationParameters = z11.object({
  prompt: z11.string().optional(),
  sourceLanguageCode: z11.custom(isValidLanguage),
  targetLanguageCode: z11.custom(isValidLanguage),
  translationExamples: dict.optional(),
  vocabulary: dict.optional(),
  temperature: z11.number().min(0).max(1).optional(),
  instructions: dict.optional()
});
var isTextTranslationParameters = (data) => textTranslationParameters.safeParse(data).success;

// src/utils/assert.ts
var assert = (value, message = "Value is not defined") => {
  if (value === null || value === void 0) {
    throw new Error(message);
  }
};

// src/utils/color.ts
function uuidToColor(uuid) {
  const parts = uuid.split("-");
  return `#${parts[0]?.substring(0, 2)}${parts[1]?.substring(
    0,
    2
  )}${parts[2]?.substring(0, 2)}`;
}

// src/utils/jwt.ts
var parseJWT = (token) => {
  const payloadBase64 = token.split(".")[1];
  if (!payloadBase64) {
    throw new Error("Invalid token");
  }
  const payloadJson = atob(payloadBase64);
  return JSON.parse(payloadJson);
};

// src/utils/lodash.ts
import groupBy from "lodash.groupby";
import keyBy from "lodash.keyby";
import uniq from "lodash.uniq";

// src/utils/mime-type.ts
var inferMimeTypeFromExtension = (blobRef) => {
  const extension = blobRef.split(".").pop();
  switch (extension) {
    case "txt":
      return "text/plain" /* TXT */;
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document" /* DOCX */;
    case "pdf":
      return "application/pdf" /* PDF */;
    case "mp3":
      return "audio/mpeg" /* MP3 */;
    case "jpeg":
      return "image/jpeg" /* JPEG */;
    case "png":
      return "image/png" /* PNG */;
    case "gif":
      return "image/gif" /* GIF */;
    case "webp":
      return "image/webp" /* WEBP */;
    case "avif":
      return "image/avif" /* AVIF */;
    case "mp4":
      return "video/mp4" /* MP4 */;
    case "webm":
      return "video/webm" /* WEBM */;
    case "xml":
      return "application/xml" /* XML_APPLICATION */;
    default:
      throw new Error(`Unsupported extension: ${extension}`);
  }
};
var isValidMimeType = (mimeType) => Object.values(MimeType).includes(mimeType);
var isBinaryMimeType = (mimeType) => [
  "image/png" /* PNG */,
  "image/apng" /* APNG */,
  "image/jpeg" /* JPEG */,
  "image/gif" /* GIF */,
  "image/webp" /* WEBP */,
  "image/avif" /* AVIF */,
  "audio/mpeg" /* MP3 */,
  "video/mp4" /* MP4 */,
  "video/webm" /* WEBM */,
  "application/pdf" /* PDF */
].includes(mimeType);
var isAudioMimeType = (mimeType) => [
  "audio/aac" /* AAC */,
  "audio/midi" /* MIDI */,
  "audio/mpeg" /* MP3 */,
  "audio/ogg" /* OGA */,
  "audio/wav" /* WAV */,
  "audio/webm" /* WEBA */
].includes(mimeType);

// src/utils/partition.ts
var partition = (array, predicate) => array.reduce(
  ([pass, fail], item) => predicate(item) ? [[...pass, item], fail] : [pass, [...fail, item]],
  [[], []]
);

// src/utils/retry-fetch.ts
async function fetchWithRetry(input, init, retries = 3, backoff = 300) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetch(input, init);
      if (!response.ok && (response.status >= 500 || response.status === 429 || response.status >= 400)) {
        throw new Error(`HTTP error: ${response.status}`);
      }
      return response;
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise((res) => setTimeout(res, backoff * 2 ** attempt));
      }
    }
  }
  throw lastError;
}

// src/utils/s3.ts
var extractBucketFromBlobRef = (blobRef) => {
  const bucket = blobRef.split("https://")[1]?.split(".")[0];
  if (!bucket) {
    throw new Error("Invalid blob reference");
  }
  return bucket;
};
var streamToBuffer = async (stream) => {
  const chunks = [];
  return new Promise((resolve, reject) => {
    stream.on("data", (chunk) => {
      chunks.push(typeof chunk === "string" ? Buffer.from(chunk) : chunk);
    });
    stream.on("end", () => {
      resolve(Buffer.concat(chunks));
    });
    stream.on("error", reject);
  });
};
export {
  APP_NAME,
  Constants,
  ContentModel,
  MimeType,
  SQSConsumerEnv,
  ServerEnv,
  Statistic,
  TASK_COLOR,
  Task,
  WORD_TRANSLATION_PROVIDER_ID,
  assert,
  audioGenerationParameters,
  baseMetric,
  date2 as date,
  describeImageMetrics,
  describeImageParameters,
  dict,
  extractBucketFromBlobRef,
  extractStructureMetrics,
  extractStructureParameters,
  fetchWithRetry,
  formatter,
  generateAudioMetrics,
  groupBy,
  inferMimeTypeFromExtension,
  isAudioGenerationParameters,
  isAudioMimeType,
  isBinaryMimeType,
  isDescribeImageMetrics,
  isDescribeImageParameters,
  isExtractStructureMetrics,
  isExtractStructureParameters,
  isGenerateAudioMetrics,
  isS3File,
  isTextTranslationMetrics,
  isTextTranslationParameters,
  isValidLanguage,
  isValidMimeType,
  keyBy,
  parseJWT,
  partition,
  streamToBuffer,
  textTranslationMetrics,
  textTranslationParameters,
  uniq,
  uuidToColor
};
